import { metricKeys } from '@pulse/metrics';
import remove from 'lodash/remove';
import { DEFAULT_DIMENSIONS } from '@store/trends/base/reducer';
import { getMetricDisplayName, getMetricShortName, getMetricMetaByKey } from '../metrics';
import {
  DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC,
  DEFAULT_SENSITIVITY_CONFIG_FOR_APP_METRIC,
  DEFAULT_SENSITIVITY_CONFIG_FOR_AD_METRIC,
} from './mapping';
import { PRODUCT_MODE_MAP } from '../constants';

export const APP_AI_ALERT_METRIC_LIST = [
  'APP_PAGE_LOAD_TIME',
  'APP_SCREEN_LOAD_TIME',
  'APP_NETWORK_REQUEST_TIME',
  'APP_ACTIVE_DEVICES',
];

export const ALERT_TYPE_CONTEXT_MAP = {
  AD_ALERT: PRODUCT_MODE_MAP.AI,
  APP_ALERT: PRODUCT_MODE_MAP.APP,
  VIDEO_ALERT: PRODUCT_MODE_MAP.EI,
  ECO_ALERT: PRODUCT_MODE_MAP.ECO_SERVER,
};

export const aiAlertMapping = {
  AUTOALERTVSF: metricKeys.VideoStartFailures,
  AUTOALERTVST: metricKeys.VideoStartTime,
  AUTOALERTEBVS: metricKeys.ExitBeforeVideoStarts,
  AUTOALERTCIRR: metricKeys.ConnectionInducedRebufferingRatio,
  AUTOALERTVPF: metricKeys.VideoPlaybackFailures,
  AUTOALERTPCP: metricKeys.ConcurrentPlays,
  AD_AUTOALERTVSF: metricKeys.AdVideoStartFailures,
  AD_AUTOALERTBUFFERINGRATIO: metricKeys.AdRebufferingRatio,
  AD_AUTOALERTBITRATE: metricKeys.AdBitrate,
  AD_AUTOALERTSLATEDURATIONRATIO: metricKeys.AdSlateDurationPct,
  // APP mapping
  APP_PAGE_LOAD_TIME: metricKeys.AppAvgPageLoadTime,
  APP_SCREEN_LOAD_TIME: metricKeys.AppAvgScreenLoadTime,
  APP_NETWORK_REQUEST_TIME: metricKeys.AverageRequestDuration,
  APP_ACTIVE_DEVICES: metricKeys.AppUniqueDevices,
};

export const aiAlertSensitivityMetricMapping = {
  AUTOALERTVSF: metricKeys.VideoStartFailures,
  AUTOALERTVST: metricKeys.VideoStartTime,
  AUTOALERTEBVS: metricKeys.ExitBeforeVideoStarts,
  AUTOALERTCIRR: metricKeys.ConnectionInducedRebufferingRatio,
  AUTOALERTVPF: metricKeys.VideoPlaybackFailures,
  AUTOALERTPCP: metricKeys.ConcurrentPlays,
  AD_AUTOALERTVSF: metricKeys.AdVideoStartFailures,
  AD_AUTOALERTBUFFERINGRATIO: metricKeys.AdRebufferingRatio,
  AD_AUTOALERTBITRATE: metricKeys.AdBitrate,
  AD_AUTOALERTSLATEDURATIONRATIO: metricKeys.AdSlateDurationPct,
  // APP mapping
  AIALERTPAGELOADTIME: metricKeys.AppAvgPageLoadTime,
  AIALERTSCREENLOADTIME: metricKeys.AppAvgScreenLoadTime,
  AIALERTNETWORKREQUESTTIME: metricKeys.AverageRequestDuration,
  AIALERTACTIVEDEVICES: metricKeys.AppUniqueDevices,
};

export const adAiAlertUnits = {
  AD_AUTOALERTVSF: '%',
  AD_AUTOALERTBUFFERINGRATIO: '%',
  AD_AUTOALERTBITRATE: 'mbps',
  AD_AUTOALERTSLATEDURATIONRATIO: '%',
};

export const productMap = {
  [PRODUCT_MODE_MAP.EI]: 'ei',
  [PRODUCT_MODE_MAP.AI]: 'ai',
  [PRODUCT_MODE_MAP.APP]: 'ae',
};

export const invertProductMap = {
  ei: PRODUCT_MODE_MAP.EI,
  ai: PRODUCT_MODE_MAP.AI,
  ae: PRODUCT_MODE_MAP.APP,
};

const EI_ALERT_DIAGNOSTICS_DIMENSIONS = [
  'OBJECT_NAME',
  'm3.dv.n',
  'LIVE_VIDEO',
  'CDN',
  'GEO_ISP',
  'm3.dv.osf',
];

// summary metrics
const getAIAlertsMetricsMeta = () => [
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.VideoPlaybackFailures,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.VideoPlaybackFailures,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.VideoPlaybackFailures,
    pulse4Key: 'AUTOALERTVPF',
    type: PRODUCT_MODE_MAP.EI,
    unit: '%',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.VideoStartFailures,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.VideoStartFailures,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.VideoStartFailures,
    pulse4Key: 'AUTOALERTVSF',
    type: PRODUCT_MODE_MAP.EI,
    unit: '%',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.VideoPlaybackFailuresTech,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.VideoPlaybackFailuresTech,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.VideoPlaybackFailuresTech,
    pulse4Key: 'AUTOALERTVPF',
    type: PRODUCT_MODE_MAP.EI,
    unit: '%',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.VideoStartFailuresTech,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.VideoStartFailuresTech,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.VideoStartFailuresTech,
    pulse4Key: 'AUTOALERTVSF',
    type: PRODUCT_MODE_MAP.EI,
    unit: '%',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.VideoStartTime,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.VideoStartTime,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.VideoStartTime,
    pulse4Key: 'AUTOALERTVST',
    type: PRODUCT_MODE_MAP.EI,
    unit: 'sec',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.ExitBeforeVideoStarts,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.ExitBeforeVideoStarts,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.ExitBeforeVideoStarts,
    pulse4Key: 'AUTOALERTEBVS',
    type: PRODUCT_MODE_MAP.EI,
    unit: '%',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.ConnectionInducedRebufferingRatio,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.ConnectionInducedRebufferingRatio,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.ConnectionInducedRebufferingRatio,
    pulse4Key: 'AUTOALERTCIRR',
    type: PRODUCT_MODE_MAP.EI,
    unit: '%',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.ConcurrentPlays,
      context: PRODUCT_MODE_MAP.EI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.ConcurrentPlays,
      context: PRODUCT_MODE_MAP.EI,
    }),
    isCustom: false,
    metricKey: metricKeys.ConcurrentPlays,
    pulse4Key: 'AUTOALERTPCP',
    type: PRODUCT_MODE_MAP.EI,
    unit: '',
    secondaryMetric: metricKeys.Attempts,
    dimensionList: EI_ALERT_DIAGNOSTICS_DIMENSIONS,
  },
  {
    name: getMetricDisplayName({ metricKey: metricKeys.Bitrate, context: PRODUCT_MODE_MAP.AI }),
    shortName: getMetricShortName({
      metricKey: metricKeys.Bitrate,
      context: PRODUCT_MODE_MAP.AI,
    }),
    isCustom: false,
    pulse4Key: 'AD_AUTOALERTBITRATE',
    metricKey: metricKeys.AdBitrate,
    type: PRODUCT_MODE_MAP.AI,
    unit: 'bps',
    dimensionList: DEFAULT_DIMENSIONS[PRODUCT_MODE_MAP.AI],
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.VideoStartFailures,
      context: PRODUCT_MODE_MAP.AI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.VideoStartFailures,
      context: PRODUCT_MODE_MAP.AI,
    }),
    isCustom: false,
    pulse4Key: 'AD_AUTOALERTVSF',
    metricKey: metricKeys.AdVideoStartFailures,
    type: PRODUCT_MODE_MAP.AI,
    unit: '%',
    dimensionList: DEFAULT_DIMENSIONS[PRODUCT_MODE_MAP.AI],
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.RebufferingRatio,
      context: PRODUCT_MODE_MAP.AI,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.RebufferingRatio,
      context: PRODUCT_MODE_MAP.AI,
    }),
    isCustom: false,
    pulse4Key: 'AD_AUTOALERTBUFFERINGRATIO',
    metricKey: metricKeys.AdRebufferingRatio,
    type: PRODUCT_MODE_MAP.AI,
    unit: '%',
    dimensionList: DEFAULT_DIMENSIONS[PRODUCT_MODE_MAP.AI],
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.AppAvgPageLoadTime,
      context: PRODUCT_MODE_MAP.APP,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.AppAvgPageLoadTime,
      context: PRODUCT_MODE_MAP.APP,
    }),
    pulse4Key: 'APP_PAGE_LOAD_TIME',
    isCustom: false,
    metricKey: metricKeys.AppAvgPageLoadTime,
    type: PRODUCT_MODE_MAP.APP,
    unit: 'sec',
    dimensionList: [
      'page_title',
      'page_urlhost',
      'page_urlpath',
      'browser_name',
      'device_name',
      'app_id',
      'dt_1',
    ],
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.AppAvgScreenLoadTime,
      context: PRODUCT_MODE_MAP.APP,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.AppAvgScreenLoadTime,
      context: PRODUCT_MODE_MAP.APP,
    }),
    pulse4Key: 'APP_SCREEN_LOAD_TIME',
    metricKey: metricKeys.AppAvgScreenLoadTime,
    type: PRODUCT_MODE_MAP.APP,
    isCustom: false,
    unit: 'sec',
    dimensionList: [
      'screen_title',
      'app_id',
      'application_version',
      'device_name',
      'da_model',
      'dt_1',
    ],
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.AverageRequestDuration,
      context: PRODUCT_MODE_MAP.APP,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.AverageRequestDuration,
      context: PRODUCT_MODE_MAP.APP,
    }),
    pulse4Key: 'APP_NETWORK_REQUEST_TIME',
    metricKey: metricKeys.AverageRequestDuration,
    type: PRODUCT_MODE_MAP.APP,
    isCustom: false,
    unit: 'sec',
    dimensionList: [
      'network_request_url_host',
      'network_request_url_path',
      'network_request_response_code',
      'app_id',
      'device_name',
      'dt_1',
    ],
  },
  {
    name: getMetricDisplayName({
      metricKey: metricKeys.AppUniqueDevices,
      context: PRODUCT_MODE_MAP.APP,
    }),
    shortName: getMetricShortName({
      metricKey: metricKeys.AppUniqueDevices,
      context: PRODUCT_MODE_MAP.APP,
    }),
    isCustom: false,
    pulse4Key: 'APP_ACTIVE_DEVICES',
    metricKey: metricKeys.AppUniqueDevices,
    type: PRODUCT_MODE_MAP.APP,
    unit: '',
    dimensionList: ['app_id', 'device_name', 'application_version', 'dt_1'],
  },
];

const handleBLEFeature = (aiAlertsMetricsStaticMeta, isBusinessConfigEnabled = false) => {
  if (!isBusinessConfigEnabled) {
    remove(aiAlertsMetricsStaticMeta, {
      metricKey: metricKeys.VideoStartFailuresTech,
      type: PRODUCT_MODE_MAP.EI,
    });
    remove(aiAlertsMetricsStaticMeta, {
      metricKey: metricKeys.VideoPlaybackFailuresTech,
      type: PRODUCT_MODE_MAP.EI,
    });
  } else {
    remove(aiAlertsMetricsStaticMeta, {
      metricKey: metricKeys.VideoStartFailures,
      type: PRODUCT_MODE_MAP.EI,
    });
    remove(aiAlertsMetricsStaticMeta, {
      metricKey: metricKeys.VideoPlaybackFailures,
      type: PRODUCT_MODE_MAP.EI,
    });
  }
  return aiAlertsMetricsStaticMeta;
};

export const DEFAULT_DIMENSIONS_FOR_AI_ALERTS_DIAGNOSTICS = {
  APP: [
    'network_request_url_host',
    'network_request_url_path',
    'network_request_response_code',
    'network_request_url_query',
    'device_name',
    'page_url',
    'ip_isp',
    'event_name',
  ],
  EI: [],
  AI: [],
};

const createMetricConfigForUserDefinedMetrics = (customMetricList) => {
  const metricConfig = customMetricList.map((metric) => {
    const metricInfo = getMetricMetaByKey({
      metricKey: metric,
      context: PRODUCT_MODE_MAP.APP,
    });
    return {
      name: metricInfo.displayName,
      shortName: metricInfo.short,
      metricKey: metric,
      pulse4Key: metric,
      type: PRODUCT_MODE_MAP.APP,
      isCustom: true,
      unit: metricInfo.unit,
      dimensionList: DEFAULT_DIMENSIONS_FOR_AI_ALERTS_DIAGNOSTICS[PRODUCT_MODE_MAP.APP],
    };
  });
  return metricConfig;
};

export const getAIAlertsMetricsMetadata = (featureFlags, sensitivityMetricList) => {
  const {
    isAdAlertsEnabled,
    isContentAlertsEnabled,
    isAppAlertEnabled,
    isPcpEnabled,
    isBusinessMetricsEnabled,
  } = featureFlags;
  let aiAlertsMetricsStaticMeta = getAIAlertsMetricsMeta();
  const customMetricList = sensitivityMetricList.filter(
    (metric) => !aiAlertSensitivityMetricMapping[metric],
  );
  const userDefinedMetricConfig = createMetricConfigForUserDefinedMetrics(customMetricList);
  aiAlertsMetricsStaticMeta = aiAlertsMetricsStaticMeta.concat(userDefinedMetricConfig);
  aiAlertsMetricsStaticMeta = handleBLEFeature(aiAlertsMetricsStaticMeta, isBusinessMetricsEnabled);
  if (!isAdAlertsEnabled) {
    remove(aiAlertsMetricsStaticMeta, { type: PRODUCT_MODE_MAP.AI });
  }
  if (!isContentAlertsEnabled) {
    remove(aiAlertsMetricsStaticMeta, { type: PRODUCT_MODE_MAP.EI });
  }
  if (!isAppAlertEnabled) {
    remove(aiAlertsMetricsStaticMeta, { type: PRODUCT_MODE_MAP.APP });
  }
  if (!isPcpEnabled) {
    remove(aiAlertsMetricsStaticMeta, { metricKey: metricKeys.ConcurrentPlays });
  }
  return aiAlertsMetricsStaticMeta;
};

export const prepareDefaultSensitivityConfig = (metricKey, metric, type) => {
  switch (type) {
    case PRODUCT_MODE_MAP.EI:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC, metric, metricKey };
    case PRODUCT_MODE_MAP.APP:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_APP_METRIC, metric, metricKey };
    case PRODUCT_MODE_MAP.AI:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_AD_METRIC, metric, metricKey };
    default:
      return { ...DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC, metric, metricKey };
  }
};

export const getDefaultMetricsSensitivityConfig = (sensitivityData, aiAlertsMetricsMeta) => {
  const defaultMetricsSensitivity = aiAlertsMetricsMeta.reduce((acc, metricInfo) => {
    const { metricKey, type, pulse4Key } = metricInfo;
    let sensitivityConfig = sensitivityData.find((sensitivity) => sensitivity.metric === pulse4Key);
    if (!sensitivityConfig) {
      sensitivityConfig = prepareDefaultSensitivityConfig(metricKey, pulse4Key, type);
    } else {
      sensitivityConfig = {
        ...sensitivityConfig,
        metric: pulse4Key,
        metricKey,
      };
    }
    return [...acc, sensitivityConfig];
  }, []);
  return defaultMetricsSensitivity;
};

const AI_ALERTS_METRICS = getAIAlertsMetricsMeta();

// LEGACY CODE
export const aiAlertAdMetrics = AI_ALERTS_METRICS.filter(
  (obj) => obj.type == PRODUCT_MODE_MAP.AI,
).map((m) => m.pulse4Key);

export const getAiAlertsMetrics = (isBusinessConfigEnabled = false) => {
  const AIAlertsMetrics = getAIAlertsMetricsMeta();
  if (!isBusinessConfigEnabled) {
    remove(AIAlertsMetrics, { metricKey: metricKeys.VideoStartFailuresTech });
    remove(AIAlertsMetrics, { metricKey: metricKeys.VideoPlaybackFailuresTech });
  } else {
    remove(AIAlertsMetrics, { metricKey: metricKeys.VideoStartFailures });
    remove(AIAlertsMetrics, { metricKey: metricKeys.VideoPlaybackFailures });
  }
  return AIAlertsMetrics;
};
// LEGACY CODE

export type AIAlertMetric = (typeof AI_ALERTS_METRICS)[number];
